// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import round from 'lodash/round';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Selectors
import { selectShelfStatusedPortfolioHoldings } from '../../../redux/modules/shelf-management/selectors';

// Components
import { LoadingSpinner } from '../common';
import { SunburstChart } from '../charts';
import GrowthChart from '../charts/deprecated/charts/GrowthChart';
import AssetAllocationDisclaimer from '../disclaimers/comparison/AssetAllocationDisclaimer';
import EditPortfolioHolding from './EditPortfolioHolding';
import { withErrorBoundary } from '../errors';
import HoldingsEditor from '../holdings-editor/HoldingsEditor';
import { DiscardPortfolioModal, DiscardPortfolioRiskProfileModal } from '../modals';
import EditPortfolioQuickSearch from './EditPortfolioQuickSearch';
import { SecondaryButton } from '../buttons';
import ConnectedPrivateComponent from '../PrivateComponent';

// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import { portfolioHoldingsToComparisonItems } from '@/utils/comparison';
import { HOLDING_TYPES, getTotalPortfolioAllocation, hasAllocationsChanged } from '@/utils/portfolios';
import { showError } from '@/utils/errors';
import notification from '@/utils/notification';
import { canAccessAllocationModeToggle } from '@/utils/feature-access';
import { getContactName } from '@/utils/contacts';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { HOLDINGS_EDITOR_TYPE, TIME_RANGE_PRESET } from '@/utils/constants';
import { generateFundFromCustomYieldConfig } from '@/utils/portfolio/utils';
import { ShelfStatus as ShelfStatusEnum } from '@/utils/shelf-management';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import helpers from '@/utils/disclaimers/helpers';
import { onAddItem } from './utils/legacy-quick-search-portfolio-actions';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import {
  addAltToPortfolioById,
  addFundToPortfolioById,
  addStockToPortfolioById,
  allocateEvenly,
  allocateRemaining,
  calculatePortfolio,
  cancelPortfolioEditing,
  changeHoldingDollars,
  changeHoldingSymbol,
  changeHoldingUnits,
  changeHoldingWeight,
  clearPortfolio,
  deletePortfolioRiskProfile,
  removeHoldingFromPortfolio,
  resetPortfolio,
  setComparisonItemName,
  setHoldingToCompareComplianceAlts,
  setPortfolioAllocationMode,
  setPortfolioCanSave,
  setPortfolioContentOption,
  setPortfolioDiscretionaryMer,
  setPortfolioIsCalculating,
  setPortfolioName,
  setPortfolioSize,
  updatePortfolioHolding,
} from '../../../redux/modules/edit-portfolio';
import { togglePortfolioEditor } from '../../../redux/modules/common/actionCreators';
import { getFundData } from '../../../redux/modules/funds/fund-store';
import { getStockData } from '../../../redux/modules/stocks/stock-store';
import { addSearchItemLog } from '../../../redux/modules/activity-log';
import { setRiskProfileForEdit } from '../../../redux/modules/kyc-questionnaire';
import { PortfolioBuilderContentOption, PortfolioCalculationPromiseReturn } from '../../../redux/modules/types/edit-portfolio';
import { addFundComplianceAlternatives, ADD_COMPLIANCE_ALTERNATIVES } from '../../../redux/modules/compliance-alternatives';
import { selectIsLoadingEvery, selectIsLoadingParamsEvery } from '../../../redux/modules/ui/loaders';

// Types
import type { EditablePortfolioFields } from '../types/portfolios';
import Dictionary from '@/types/dictionary';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import Nullable from '@/types/nullable';
import ComparisonItem from '@/types/comparison-item';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import CancellablePromise from '@/types/cancellable-promise';
import Locale from '@/types/locale';
import AppRegion from '@/types/app-region';
import ThemeMode from '@/types/theme-mode';
import { getAltData } from '@/redux/modules/alts/alts-store';
import Alt from '@/types/models/alt';
import { QuickSearchV2 } from '../search/quick-search-v2';
import { QUICK_SEARCH_REDUCER_NAME } from '@/redux/modules/quick-search';
import { getThemeVersion } from '@/utils/theme';
import { QUICK_SEARCH_FILTERS } from '@/enums/QuickSearch';
import { addAltToPortfolio, addFundToPortfolio, addStockToPortfolio } from './utils/quick-search-portfolio-actions';
import { getShelfNameFromShelvableInstrument } from '@/utils/user-shelf';

type DispatchProps = {
  addAltToPortfolioById: (id: string) => Promise<void>;
  addFundComplianceAlternatives: (id: string) => Promise<string>;
  addFundToPortfolioById: (id: string) => Promise<void>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  addStockToPortfolioById: (id: string) => Promise<void>;
  allocateEvenly: () => void;
  allocateRemaining: () => void;
  calculatePortfolio: (originalItem?: ComparisonItem, reallocate?: boolean) => Promise<void>;
  cancelPortfolioEditing: () => void;
  changeHoldingDollars: (id: string, dollars: number, holding: Holding) => void;
  changeHoldingSymbol: (id: string, symbol: string) => void;
  changeHoldingUnits: (id: string, units: number, holding: Holding) => void;
  changeHoldingWeight: (id: string, weight: number, holding: Holding) => void;
  clearPortfolio: () => void;
  deletePortfolioRiskProfile: () => void;
  displayRiskProfile: () => void;
  getAltData: (id: string) => Promise<Alt>;
  getFundData: (id: string) => Promise<Fund>;
  getStockData: (id: string) => Promise<Stock>;
  onSelectHoldingMissing?: (holding?: PortfolioHolding, selected?: boolean) => void;
  removeHoldingFromPortfolio: (holding: PortfolioHolding) => void;
  resetPortfolio: () => void;
  setComparisonItemName: (name: string) => void;
  setHoldingToCompareComplianceAlts: (holding?: PortfolioHolding) => void;
  setPortfolioAllocationMode: (mode: HoldingAllocationMode) => void;
  setPortfolioCanSave: (canSave: boolean) => void;
  setPortfolioContentOption: (option: PortfolioBuilderContentOption) => void;
  setPortfolioDiscretionaryMer: (mer: Nullable<number>) => void;
  setPortfolioIsCalculating: (isCalculating: boolean) => void;
  setPortfolioName: (name: string) => void;
  setPortfolioSize: (size: Nullable<number>) => void;
  setRiskProfileForEdit: (profile: PortfolioRiskProfile) => void;
  togglePortfolioEditor: (isVisible: boolean, isPinned?: boolean, isFocused?: boolean, isReadOnly?: boolean) => void;
  updatePortfolioHolding: (holding: PortfolioHolding) => void;
};

type StateProps = {
  portfolio: Portfolio;
  comparisonItem?: ComparisonItem;
  user?: Nullable<User>;
  isCalculating: boolean;
  calculationPromise?: Nullable<CancellablePromise<PortfolioCalculationPromiseReturn>>;
  isSaving: boolean;
  isLoading: boolean;
  isPortfolioDirty: boolean;
  isPinned?: boolean;
  complianceAlternatives: Fund[];
  addingAlternativeIds: string[];
  locale?: Locale;
  isReadOnly?: boolean;
  hasFileUploading?: boolean;
  theme?: ThemeMode;
};

type Props = DispatchProps &
  StateProps & {
    onSave: (portfolio: Portfolio) => Promise<Portfolio>;
    onCancel?: (portfolio: Portfolio) => void;
    onDeleteRiskProfile?: () => void;
    onPin?: () => void;
    chartsDelay?: number;
    color?: string;
    className?: string;
    withCharts?: boolean;
    dark?: boolean;
    showAssetDetails?: boolean;
    splitCashAndFixedIncome?: boolean;
    showDollars: boolean;
    showMER: boolean;
    disableSaveOptions?: boolean;
    isReadOnly?: boolean;
    showQuickSearch?: boolean;
    saveLabel?: string;
    saveNotification?: string;
    sidebarContentOption: PortfolioBuilderContentOption;
    holdingSelected?: PortfolioHolding;
    hasMissingHoldings?: boolean;
    allowComplianceComparisons: boolean;
    componentIsReady?: () => void;
    canDeselect?: boolean;
    isRiskProfileView?: boolean;
  };

type State = {
  saveIsDisabled: boolean;
  showCharts?: boolean;
  chartsTimeoutId?: number;
  calculateTimeoutId?: number;
  holdingsData: Dictionary<Holding>;
  showDiscardPortfolioModal: boolean;
  showDiscardPortfolioRiskProfileModal: boolean;
  editableFields: EditablePortfolioFields;
};

const NEW_PORTFOLIO_COLOR = '#a5adba';

class EditPortfolio extends Component<Props, State> {
  private _isMounted = false;

  editorRef = React.createRef<HoldingsEditor>();

  holdingRefs: Dictionary<EditPortfolioHolding | null> = {};

  runPortfolioCalculations = debounce(() => {
    this.setState({ showCharts: true });
    this.props.calculatePortfolio(this.props.comparisonItem).catch(showError);
  }, 500);

  static defaultProps = {
    withCharts: true,
    showDollars: true,
    showMER: true,
    showQuickSearch: true,
    allowComplianceComparisons: true,
  };

  initializeEditableFields = () => {
    const { isReadOnly, comparisonItem } = this.props;

    const editAll = {
      name: true,
      size: true,
      mer: true,
      holdings: true,
    };

    const holdingsReadOnly = {
      name: true,
      size: true,
      mer: true,
      holdings: false,
    };

    const readOnly = {
      name: false,
      size: false,
      mer: false,
      holdings: false,
    };

    switch (true) {
      case isReadOnly && !comparisonItem?.enterprise && !comparisonItem?.providedBy?.id && !!comparisonItem?.overrideHoldings?.length:
        return holdingsReadOnly;
      case isReadOnly:
        return readOnly;
      default:
        return editAll;
    }
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      saveIsDisabled: false,
      holdingsData: {},
      showDiscardPortfolioModal: false,
      showDiscardPortfolioRiskProfileModal: false,
      editableFields: this.initializeEditableFields(),
    };
  }

  componentWillMount() {
    // Set timeout on charts appearing for when the component is animated in.
    // This gets rid of chart sizing issues
    if (this.props.chartsDelay) {
      const chartsTimeoutId = window.setTimeout(() => {
        this.setState({ showCharts: true });
      }, this.props.chartsDelay);
      this.setState({ chartsTimeoutId });
    }
  }

  componentDidMount() {
    const { holdingSelected, componentIsReady } = this.props;

    this._isMounted = true;

    this.fetchPortfolioHoldingsData();

    const calculateTimeoutId = window.setTimeout(() => {
      if (this._isMounted) {
        const allocation = getTotalPortfolioAllocation(this.props.portfolio);
        if ((round(allocation, 2) === 100 || this.props.portfolio.allocationMode === HoldingAllocationMode.Units) && !this.props.hasMissingHoldings) {
          this.props
            .calculatePortfolio(this.props.comparisonItem, true)
            .catch(showError)
            .finally(() => componentIsReady && componentIsReady());
        } else {
          componentIsReady && componentIsReady();
        }
      }
    }, 500);
    this.setState({ calculateTimeoutId });

    if (holdingSelected) {
      setTimeout(() => {
        this.focusHolding(holdingSelected);
      }, 0);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const saveIsDisabled = this.saveIsDisabled();
    if (saveIsDisabled !== prevState.saveIsDisabled) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ saveIsDisabled });
      // Persist the status of the save button to Redux so this status
      // can be used in other components to drive UI updates.
      this.props.setPortfolioCanSave(!saveIsDisabled);
    }
    const newTotalAllocation = getTotalPortfolioAllocation(this.props.portfolio);

    const hasNewHolding = this.props.portfolio.holdings.some(holding => prevProps.portfolio.holdings.every(h => holding.original.id !== h.original.id));

    if (hasNewHolding || (!this.props.hasMissingHoldings && prevProps.hasMissingHoldings)) {
      this.fetchPortfolioHoldingsData();
    }

    if (this.props.holdingSelected && this.props.holdingSelected._id !== prevProps.holdingSelected?._id) {
      this.focusHolding(this.props.holdingSelected);
    }

    // Don't calculate portfolio stats if not fully allocated.
    if (round(newTotalAllocation, 2) === 100) {
      const allocationHasChanged = hasAllocationsChanged(prevProps.portfolio.holdings, this.props.portfolio.holdings);
      const merHasChanged = prevProps.portfolio.discretionaryMer !== this.props.portfolio.discretionaryMer;
      const sizeHasChanged = prevProps.portfolio.size !== this.props.portfolio.size;

      if ((allocationHasChanged || merHasChanged || sizeHasChanged) && !this.props.hasMissingHoldings) {
        this.calculatePortfolio();
      }
    }

    if (this.props.portfolio !== prevProps.portfolio) {
      this.setState({ editableFields: this.initializeEditableFields() });
    }

    if (this.props.isReadOnly !== prevProps.isReadOnly) {
      this.setState({ editableFields: this.initializeEditableFields() });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    // Prevents leaks in case component gets unmounted before animation ends
    window.clearTimeout(this.state.chartsTimeoutId);
    window.clearTimeout(this.state.calculateTimeoutId);
  }

  calculatePortfolio = () => {
    if (!this.props.hasMissingHoldings) {
      // For safety, calculation promise also gets cancelled when the actually calculations are fired off.
      // However those calculations are debounced, and we want the promise to be cancelled immediately - hence
      // the need to also cancel the promise here.
      this.props.calculationPromise?.cancel();

      this.props.setPortfolioIsCalculating(true);
      this.runPortfolioCalculations();
    }
  };

  fetchPortfolioHoldingsData = () => {
    const { portfolio, hasMissingHoldings } = this.props;
    if (!hasMissingHoldings) {
      Promise.all(portfolio.holdings.map(this.getHoldingData)).then(data => {
        const holdingsData = data.reduce((acc, datum) => {
          acc[datum._id] = datum;
          return acc;
        }, {} as Dictionary<Holding>);
        this.setState({ holdingsData });
      });
    }
  };

  portfolioIsFull = (): boolean => {
    const { portfolio } = this.props;

    return round(getTotalPortfolioAllocation(portfolio), 2) === 100;
  };

  onAllocateRemaining = () => {
    this.props.allocateRemaining();
  };

  onAllocateEvenly = () => {
    this.props.allocateEvenly();
  };

  onSymbolChange = async (holding: PortfolioHolding, symbol: string) => {
    this.props.changeHoldingSymbol(holding._id, symbol);
  };

  onWeightChange = async (holding: PortfolioHolding, weight: number) => {
    this.setState({ showCharts: false });

    const data = await this.getHoldingData(holding);
    this.props.changeHoldingWeight(holding._id, weight, data);
  };

  onDollarsChange = async (holding: PortfolioHolding, dollars: number) => {
    this.setState({ showCharts: false });

    const data = await this.getHoldingData(holding);
    this.props.changeHoldingDollars(holding._id, dollars, data);
  };

  onUnitsChange = async (holding: PortfolioHolding, units: number) => {
    this.setState({ showCharts: false });

    const data = await this.getHoldingData(holding);
    this.props.changeHoldingUnits(holding._id, units, data);
  };

  getHoldingData = (holding: PortfolioHolding): Promise<Holding> => {
    if (!holding.original.id) {
      return Promise.reject();
    }

    switch (holding.original.type) {
      case HOLDING_TYPES.FUND: {
        return this.props.getFundData(holding.original.id);
      }
      case HOLDING_TYPES.STOCK: {
        return this.props.getStockData(holding.original.id);
      }
      case HOLDING_TYPES.ALT: {
        return this.props.getAltData(holding.original.id);
      }
      case HOLDING_TYPES.CUSTOM_YIELD: {
        if (!holding.original.yieldParams) {
          return Promise.reject();
        }

        return Promise.resolve(
          generateFundFromCustomYieldConfig(
            holding.original.yieldParams,
            { filteredShelves: holding.filteredShelves, complianceComparisons: holding.complianceComparisons },
            holding.currency,
            holding.original.id,
          ),
        );
      }
      default: {
        return Promise.reject();
      }
    }
  };

  saveIsDisabled = () => {
    const { isSaving, portfolio, isPortfolioDirty, isCalculating, hasMissingHoldings, comparisonItem } = this.props;

    // If an item has overridden performances we let the user save to a comparison as is
    const overrideHoldingsException = !!comparisonItem?.overrideHoldings;

    const saveIsDisabled =
      (!overrideHoldingsException && hasMissingHoldings) ||
      isCalculating ||
      isSaving ||
      !portfolio.size ||
      portfolio.size <= 0 ||
      (!overrideHoldingsException && !this.portfolioIsFull()) ||
      !formatLocalizedString(portfolio.name) ||
      !isPortfolioDirty ||
      portfolio.holdings.some(holding => !holding.dollars);

    return saveIsDisabled;
  };

  onPortfolioSizeChange = (size: Nullable<number>) => {
    const roundedSize = size ? +size.toFixed(2) : null;
    this.props.setPortfolioSize(roundedSize);
  };

  onBlurPortfolioSizeChange = () => {
    const { size } = this.props.portfolio;

    if (!size) {
      this.props.clearPortfolio();
    }
  };

  onFeeBPSChange = (bps: Nullable<number>) => {
    this.setState({ showCharts: false });

    let mer: Nullable<number> = null;

    if (bps !== null) {
      mer = bps * 0.01;
    }

    this.props.setPortfolioDiscretionaryMer(mer);
  };

  onCancel = () => {
    const { portfolio, onCancel } = this.props;

    this.props.cancelPortfolioEditing();

    if (onCancel) {
      onCancel(portfolio);
    }
  };

  renderCharts = () => {
    const { portfolio, user, chartsDelay, comparisonItem, color, locale } = this.props;
    const { showCharts } = this.state;

    const totalAllocated = getTotalPortfolioAllocation(portfolio);

    if (totalAllocated !== 100 || !user) {
      return null;
    }

    return (
      <>
        {!!chartsDelay && !showCharts ? (
          <LoadingSpinner />
        ) : (
          <>
            {portfolio.allocations && portfolio.allocations.holdingTypes && (
              <SunburstChart data={portfolio.allocations.holdingTypes} height={200} color={color} />
            )}

            {comparisonItem && (
              <GrowthChart
                user={user}
                localCurrency={user.appConfig.localCurrency}
                items={[comparisonItem]}
                height={200}
                compact
                color={color}
                options={{ capital: portfolio.size }}
                locale={locale}
                dateQuickRangeValues={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.SINGLE_ITEM_DEFAULT)}
              />
            )}
          </>
        )}
      </>
    );
  };

  handleCompareAlternativesForLegacyPortfolioEditor = async (holding: PortfolioHolding) => {
    const { sidebarContentOption } = this.props;
    if (!holding.original.id) {
      return;
    }
    // If we are currently not on the ComplianceAlternative content pane
    // switch to the QuickSearch pane
    if (sidebarContentOption !== PortfolioBuilderContentOption.ComplianceAlternatives) {
      this.props.setPortfolioContentOption(PortfolioBuilderContentOption.QuickSearch);
    }
    const complianceComparisonId = await this.props.addFundComplianceAlternatives(holding.original.id);

    this.props.updatePortfolioHolding({
      ...holding,
      complianceComparisons: [...(holding.complianceComparisons || []), complianceComparisonId],
      flags: holding.flags?.filter(flag => flag !== ShelfStatusEnum.ON_SHELF),
    });
    this.props.setPortfolioContentOption(PortfolioBuilderContentOption.ComplianceAlternatives);
  };

  onCompareAlternatives = async (holding: PortfolioHolding) => {
    if (!holding.original.id) {
      return;
    }

    try {
      if (!userHasPermissionEnabled(this.props.user, PERMISSIONS.PORTFOLIO_EDITOR_BUILDER)) {
        this.handleCompareAlternativesForLegacyPortfolioEditor(holding);
      }

      this.props.setHoldingToCompareComplianceAlts(holding);
      this.props.togglePortfolioEditor(true, false);
    } catch (err) {
      this.props.setPortfolioContentOption(PortfolioBuilderContentOption.QuickSearch);
      showError(err);
    }
  };

  renderHoldings = () => {
    const {
      portfolio,
      dark,
      isPinned,
      isRiskProfileView,
      theme,
      showAssetDetails,
      splitCashAndFixedIncome,
      showDollars,
      user,
      complianceAlternatives,
      allowComplianceComparisons,
      sidebarContentOption,
      addingAlternativeIds,
    } = this.props;

    const { holdingsData } = this.state;
    const showCurrencies = !!user && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);

    return portfolio.holdings.map((holding, index) => {
      const showKYPComplianceButton =
        allowComplianceComparisons &&
        holding.original.id != null &&
        holding.original.type === HOLDING_TYPES.FUND &&
        holding.flags &&
        holding.flags.includes(ShelfStatusEnum.ON_SHELF) &&
        !holding.flags.includes(ShelfStatusEnum.ON_SHELF_KYP_COMPLETE);

      const hasComplianceAlternatives = complianceAlternatives.length > 0 && complianceAlternatives[0]._id === holding.original.id;
      const prevHoldingMissingData = index - 1 >= 0 && helpers.holdingIsMissingOrInvalid(portfolio.holdings[index - 1]);
      const nextHoldingHasMissingData = index + 1 < portfolio.holdings.length && helpers.holdingIsMissingOrInvalid(portfolio.holdings[index + 1]);
      const isComparing = !!holding.original.id && addingAlternativeIds.includes(holding.original.id);
      const shelfName = user ? getShelfNameFromShelvableInstrument(holding, user) : undefined;

      const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
      const enableTheme = !isPinned && !!isRiskProfileView ? theme : undefined;

      return (
        <>
          <EditPortfolioHolding
            key={holding._id}
            ref={ref => {
              this.holdingRefs[holding._id] = ref;
            }}
            className={classNames({
              'prev-missing-data': prevHoldingMissingData,
              'next-missing-data': nextHoldingHasMissingData,
            })}
            compact={sidebarContentOption === PortfolioBuilderContentOption.ComplianceAlternatives}
            allocationMode={portfolio.allocationMode}
            portfolioHolding={holding}
            dark={dark}
            theme={enableTheme}
            data={holding.original.id != null ? holdingsData[holding.original.id] : null}
            portfolioSize={portfolio.size}
            showAssetDetails={showAssetDetails}
            splitCashAndFixedIncome={splitCashAndFixedIncome}
            showDollars={showDollars}
            onRemoveHolding={this.onRemoveHolding}
            onSymbolChange={this.onSymbolChange}
            onWeightChange={this.onWeightChange}
            onDollarsChange={this.onDollarsChange}
            onUnitsChange={this.onUnitsChange}
            readOnly={!this.state.editableFields.holdings}
            showCurrencies={showCurrencies}
            onChangeHoldingMissing={this.onChangeHoldingMissing}
            selected={this.props.holdingSelected?._id === holding._id}
            appRegion={appRegion}
            shelfName={shelfName}
          />

          {showKYPComplianceButton && !hasComplianceAlternatives && this.state.editableFields.holdings && (
            <ConnectedPrivateComponent permissions={[PERMISSIONS.KYP]}>
              {() => (
                <SecondaryButton
                  loading={isComparing}
                  className="compare-alternatives"
                  compact
                  onClick={() => this.onCompareAlternatives(holding)}
                  icon="checkmark"
                >
                  {formatMessage(`HoldingsEditor.${isComparing ? 'ComparingAlternatives' : 'CompareAlternatives'}`)}
                </SecondaryButton>
              )}
            </ConnectedPrivateComponent>
          )}
        </>
      );
    });
  };

  updateComparisonItemName = () => {
    if (this.props.comparisonItem) {
      this.props.setComparisonItemName(formatLocalizedString(this.props.portfolio.name));
    }
  };

  onSave = () => {
    const { portfolio } = this.props;
    const { saveNotification } = this.props;
    return this.props
      .onSave(portfolio)
      .then(() => {
        notification.success(saveNotification || formatMessage('HoldingsEditor.Saved.PORTFOLIO'));
      })
      .catch(showError);
  };

  onRemoveHolding = (holding: PortfolioHolding) => {
    this.props.removeHoldingFromPortfolio(holding);
  };

  onChangeHoldingMissing = (holding: PortfolioHolding, selected: boolean) => {
    if (this.props.onSelectHoldingMissing !== undefined) {
      this.props.onSelectHoldingMissing(holding, selected);
    }
  };

  focusHolding = (holding: PortfolioHolding) => {
    const scrollRef = this.editorRef.current?.scrollBarRef;
    const holdingRef = this.holdingRefs[holding._id]?.holdingRef.current?.containerRef.current;

    if (scrollRef && holdingRef) {
      scrollRef.scrollTop = holdingRef.offsetTop - scrollRef.clientHeight / 2 + holdingRef.clientHeight / 2;
    }
  };

  handleClosingPortfolioModal = () => {
    const { isPortfolioDirty } = this.props;
    if (isPortfolioDirty) {
      this.showDiscardPortfolioModal();
    } else {
      this.onCancel();
    }
  };

  showDiscardPortfolioModal = () => this.setState({ showDiscardPortfolioModal: true });

  closeDiscardPortfolioModal = () => this.setState({ showDiscardPortfolioModal: false });

  onConfirmDiscardPortfolio = () => {
    this.closeDiscardPortfolioModal();
    this.onCancel();
  };

  getQuickSearchComponent = () => {
    const { dark, showQuickSearch, user } = this.props;
    const { isLightVersion } = getThemeVersion(user);
    const options = {
      withAdvancedScreener: false,
      hiddenTabs: [QUICK_SEARCH_FILTERS.FAVORITE, QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES, QUICK_SEARCH_FILTERS.SIMULATION],
    };

    const addFund = (id: string) => addFundToPortfolio(this.props, id);
    const addStock = (id: string) => addStockToPortfolio(this.props, id);
    const addAlt = (id: string) => addAltToPortfolio(this.props, id);

    if (!showQuickSearch) {
      return undefined;
    }

    // Legacy quick search component
    if (!isLightVersion) {
      return <EditPortfolioQuickSearch dark={dark} onAddItem={item => onAddItem(this.props, item)} />;
    }

    return (
      <QuickSearchV2
        onAddAltById={addAlt}
        onAddFundById={addFund}
        onAddStockById={addStock}
        options={options}
        targetReducer={QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO}
      />
    );
  };

  getAssetAllocationDisclaimer = () => {
    const { showAssetDetails, portfolio, user } = this.props;
    const { holdingsData } = this.state;

    if (!showAssetDetails) {
      return undefined;
    }

    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    return (
      <div className="allocations-disclaimer flex-fill">
        <AssetAllocationDisclaimer shorthand items={portfolioHoldingsToComparisonItems(portfolio.holdings, holdingsData, appRegion)} />
      </div>
    );
  };

  setRiskProfileForEdit = (profile: PortfolioRiskProfile) => {
    this.props.setRiskProfileForEdit(profile);
    this.props.displayRiskProfile();
  };

  showDiscardPortfolioRiskProfileModal = () => this.setState({ showDiscardPortfolioRiskProfileModal: true });

  closeDiscardPortfolioRiskProfileModal = () => this.setState({ showDiscardPortfolioRiskProfileModal: false });

  onDeletePortfolioRiskProfile = () => {
    this.showDiscardPortfolioRiskProfileModal();
  };

  onConfirmDiscardPortfolioRiskProfile = () => {
    this.props.deletePortfolioRiskProfile();

    if (this.props.onDeleteRiskProfile) {
      this.props.onDeleteRiskProfile();
    }

    this.closeDiscardPortfolioRiskProfileModal();
  };

  onChangeAllocationMode = (allocationMode: HoldingAllocationMode) => {
    this.props.setPortfolioAllocationMode(allocationMode);
  };

  onUnpin = () => {
    this.props.togglePortfolioEditor(true, false, undefined, this.props.isReadOnly);
  };

  getAllocationModes = () => {
    const { user } = this.props;
    const modes = [HoldingAllocationMode.Weight];

    if (user && canAccessAllocationModeToggle(user)) {
      modes.push(HoldingAllocationMode.Units);
    }

    return modes;
  };

  render() {
    const {
      dark,
      theme,
      portfolio,
      isLoading,
      comparisonItem,
      user,
      withCharts,
      sidebarContentOption,
      showAssetDetails,
      splitCashAndFixedIncome,
      showDollars,
      showMER,
      isReadOnly,
      className,
      saveLabel,
      isPinned,
      hasMissingHoldings,
      onPin,
      hasFileUploading,
      canDeselect,
      isRiskProfileView,
    } = this.props;

    const { name, holdings, size, currency, discretionaryMer, contact, riskProfile } = portfolio;

    const { showDiscardPortfolioModal, showDiscardPortfolioRiskProfileModal, holdingsData } = this.state;

    let { color } = this.props;
    color = color || (comparisonItem && comparisonItem.color) || NEW_PORTFOLIO_COLOR;

    // Prevent rendering if portfolio hasn't been loaded yet.
    if (isLoading) {
      return null;
    }

    const roundedSize: number | undefined = size ? +size.toFixed(2) : undefined;
    const enableTheme = !isPinned && !!isRiskProfileView ? theme : undefined;

    return (
      <>
        <HoldingsEditor
          ref={this.editorRef}
          className={classNames(className, sidebarContentOption, 'edit-portfolio', {
            'show-discard-modal': showDiscardPortfolioModal || showDiscardPortfolioRiskProfileModal,
          })}
          allocationMode={portfolio.allocationMode}
          allocationModes={this.getAllocationModes()}
          type={HOLDINGS_EDITOR_TYPE.PORTFOLIO}
          size={roundedSize}
          name={formatLocalizedString(name)}
          holdings={holdings}
          currency={currency}
          discretionaryMer={discretionaryMer}
          color={color}
          holdingsData={holdingsData}
          setPortfolioName={this.props.setPortfolioName}
          onNameInputBlur={this.updateComparisonItemName}
          readOnly={isReadOnly}
          dark={dark}
          theme={enableTheme}
          user={user}
          onSave={this.onSave}
          onCancel={this.handleClosingPortfolioModal}
          onUnpin={this.onUnpin}
          onPin={onPin}
          saveIsDisabled={this.state.saveIsDisabled}
          saveLabel={saveLabel || formatMessage('Portfolios.Edit.Save')}
          showMER={showMER}
          showDollars={showDollars}
          contactName={contact && getContactName(contact)}
          onPortfolioSizeChange={this.onPortfolioSizeChange}
          onBlurPortfolioSizeChange={this.onBlurPortfolioSizeChange}
          onFeeBPSChange={this.onFeeBPSChange}
          showAssetDetails={showAssetDetails}
          splitCashAndFixedIncome={splitCashAndFixedIncome}
          quickSearchComponent={this.getQuickSearchComponent()}
          onAllocateRemaining={this.onAllocateRemaining}
          onAllocateEvenly={this.onAllocateEvenly}
          withCharts={withCharts}
          renderCharts={this.renderCharts}
          customDisclaimer={this.getAssetAllocationDisclaimer()}
          holdingsComponent={this.renderHoldings()}
          totalAllocated={getTotalPortfolioAllocation(portfolio)}
          onAssignRiskProfile={this.props.displayRiskProfile}
          riskProfile={riskProfile}
          riskProfileIsLocked={!!portfolio?.planning?.planAccount}
          setRiskProfileForEdit={this.setRiskProfileForEdit}
          deletePortfolioRiskProfile={this.onDeletePortfolioRiskProfile}
          isPinned={isPinned}
          onChangeAllocationMode={this.onChangeAllocationMode}
          hasMissingHoldings={hasMissingHoldings}
          editableFields={this.state.editableFields}
          hasFileUploading={hasFileUploading}
          contact={contact}
          canDeselect={canDeselect}
        />

        <DiscardPortfolioModal show={showDiscardPortfolioModal} onConfirm={this.onConfirmDiscardPortfolio} onCancel={this.closeDiscardPortfolioModal} />

        {riskProfile && (
          <DiscardPortfolioRiskProfileModal
            show={showDiscardPortfolioRiskProfileModal}
            onConfirm={this.onConfirmDiscardPortfolioRiskProfile}
            onCancel={this.closeDiscardPortfolioRiskProfileModal}
            riskProfileLabel={formatLocalizedString(riskProfile.profile.title)}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: any): StateProps => {
  /* Setting the type of `ownProps` to `OwnProps` causes a cascade of type
   * errors around the app. Tried to fix them, but it was going more and
   * more deep as I would try to fix them. It's worth lookking deeper
   * into this at another time.
   */
  const { complianceAlternatives, comparison, editPortfolio, auth } = state;
  const aleternativesLoadingParams = selectIsLoadingParamsEvery(state, [ADD_COMPLIANCE_ALTERNATIVES.type]);
  const addingAlternativeIds = aleternativesLoadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.instrumentId) {
      return [...acc, param.instrumentId as string];
    }

    return acc;
  }, [] as string[]);

  return {
    portfolio: selectShelfStatusedPortfolioHoldings(editPortfolio.portfolio),
    comparisonItem: editPortfolio.comparisonItem,
    user: auth.user,
    isSaving: editPortfolio.isSaving,
    isLoading: editPortfolio.isLoading,
    isCalculating: editPortfolio.isCalculating,
    isPortfolioDirty: editPortfolio.isDirty,
    isPinned: comparison.portfolioEditor.isPinned,
    isReadOnly: (ownProps.isReadOnly ?? comparison.portfolioEditor.isReadOnly) || selectIsLoadingEvery(state, ['PORTFOLIO_FILE_UPLOAD']),
    hasFileUploading: selectIsLoadingEvery(state, ['PORTFOLIO_FILE_UPLOAD']),
    calculationPromise: editPortfolio.calculationPromise,
    complianceAlternatives,
    addingAlternativeIds,
    locale: state.intl.locale,
    theme: state.auth.user?.preferences?.theme ?? ThemeMode.DEFAULT,
  };
};

export default connect(mapStateToProps, {
  addAltToPortfolioById,
  addFundToPortfolioById,
  addStockToPortfolioById,
  allocateRemaining,
  allocateEvenly,
  removeHoldingFromPortfolio,
  changeHoldingSymbol,
  changeHoldingWeight,
  changeHoldingDollars,
  changeHoldingUnits,
  setPortfolioSize,
  setPortfolioName,
  setPortfolioDiscretionaryMer,
  resetPortfolio,
  calculatePortfolio,
  cancelPortfolioEditing,
  setComparisonItemName,
  getFundData,
  getStockData,
  getAltData,
  clearPortfolio,
  addSearchItemLog,
  togglePortfolioEditor,
  setRiskProfileForEdit,
  deletePortfolioRiskProfile,
  setPortfolioAllocationMode,
  addFundComplianceAlternatives,
  updatePortfolioHolding,
  setPortfolioIsCalculating,
  setPortfolioCanSave,
  setPortfolioContentOption,
  setHoldingToCompareComplianceAlts,
})(withErrorBoundary(EditPortfolio));
